import { useState, useEffect, useCallback } from 'react';
import shopifyStorefront, { Cart } from '../services/shopifyStorefront';

interface UseShopifyCartReturn {
  cart: Cart | null;
  isLoading: boolean;
  error: string | null;
  itemCount: number;
  totalPrice: string;
  addToCart: (variantId: string, quantity?: number) => Promise<void>;
  updateQuantity: (lineItemId: string, quantity: number) => Promise<void>;
  removeItem: (lineItemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  getProduct: (productId: string) => Promise<any>;
}

export const useShopifyCart = (): UseShopifyCartReturn => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize cart state and listen for updates
  useEffect(() => {
    // Set initial cart state
    setCart(shopifyStorefront.getCart());

    // Subscribe to cart updates
    const unsubscribe = shopifyStorefront.onCartUpdate((updatedCart) => {
      setCart(updatedCart);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  // Add item to cart
  const addToCart = useCallback(async (variantId: string, quantity: number = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      await shopifyStorefront.addToCart(variantId, quantity);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add item to cart');
      console.error('Add to cart error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update item quantity
  const updateQuantity = useCallback(async (lineItemId: string, quantity: number) => {
    setIsLoading(true);
    setError(null);
    try {
      if (quantity <= 0) {
        await shopifyStorefront.removeFromCart(lineItemId);
      } else {
        await shopifyStorefront.updateCartItem(lineItemId, quantity);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update item quantity');
      console.error('Update quantity error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Remove item from cart
  const removeItem = useCallback(async (lineItemId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await shopifyStorefront.removeFromCart(lineItemId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove item from cart');
      console.error('Remove item error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Clear entire cart
  const clearCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await shopifyStorefront.clearCart();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear cart');
      console.error('Clear cart error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get product details
  const getProduct = useCallback(async (productId: string) => {
    try {
      return await shopifyStorefront.getProduct(productId);
    } catch (err) {
      console.error('Get product error:', err);
      return null;
    }
  }, []);

  // Computed values
  const itemCount = cart ? shopifyStorefront.getCartItemCount() : 0;
  const totalPrice = cart ? shopifyStorefront.getCartTotal() : '0.00';

  return {
    cart,
    isLoading,
    error,
    itemCount,
    totalPrice,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    getProduct,
  };
};

export default useShopifyCart; 